import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n";
import type { CelebrationKind } from "@/context/CelebrationContext";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

type CelebrationOverlayProps = {
  open: boolean;
  kind: CelebrationKind;
  title?: string;
  listingTitle?: string;
  subtitle?: string;
  detail?: string;
  onDismiss: () => void;
};

/** Between celebration dim (330) and sheet (332) — rain over backdrop, never cover Grattis. */
const CONFETTI_Z = 331;
const CONFETTI_MS = 3800;

/**
 * Dedicated body-level canvas — never nest under a sheet/dialog with CSS
 * transform (native enter animation), or Android WebView freezes particles.
 */
function fireCelebrationBurst(): () => void {
  const canvas = document.createElement("canvas");
  canvas.setAttribute("data-bilenia-confetti", "1");
  Object.assign(canvas.style, {
    position: "fixed",
    inset: "0",
    width: "100%",
    height: "100%",
    zIndex: String(CONFETTI_Z),
    pointerEvents: "none",
  });
  document.body.appendChild(canvas);

  const shoot = confetti.create(canvas, {
    resize: true,
    useWorker: false,
  });

  const defaults = {
    startVelocity: 32,
    spread: 360,
    ticks: 140,
    gravity: 0.85,
    decay: 0.92,
    scalar: 0.95,
    // Keep motion even if OS reports reduced-motion — this is an explicit
    // celebration the user just triggered (win / buy-now).
    disableForReducedMotion: false,
  };

  const randomInRange = (min: number, max: number) =>
    Math.random() * (max - min) + min;

  const animationEnd = Date.now() + CONFETTI_MS;
  let raf = 0;
  let lastBurst = 0;

  const frame = (now: number) => {
    if (now - lastBurst >= 260) {
      lastBurst = now;
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        return;
      }
      const particleCount = Math.max(
        10,
        Math.floor(34 * (timeLeft / CONFETTI_MS)),
      );
      void shoot({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.12, 0.32), y: Math.random() * 0.32 },
      });
      void shoot({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.68, 0.88), y: Math.random() * 0.32 },
      });
    }
    if (Date.now() < animationEnd) {
      raf = window.requestAnimationFrame(frame);
    }
  };

  void shoot({
    ...defaults,
    particleCount: 90,
    origin: { x: 0.5, y: 0.32 },
  });
  raf = window.requestAnimationFrame(frame);

  const hardStop = window.setTimeout(() => {
    window.cancelAnimationFrame(raf);
    try {
      shoot.reset();
    } catch {
      /* ignore */
    }
    canvas.remove();
  }, CONFETTI_MS + 400);

  return () => {
    window.clearTimeout(hardStop);
    window.cancelAnimationFrame(raf);
    try {
      shoot.reset();
    } catch {
      /* ignore */
    }
    canvas.remove();
  };
}

function useBodyScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    const blockTouch = (e: TouchEvent) => {
      // Allow scrolling inside the celebration panel itself.
      const target = e.target as HTMLElement | null;
      if (target?.closest?.("[data-celebration-panel]")) return;
      e.preventDefault();
    };
    document.addEventListener("touchmove", blockTouch, { passive: false });

    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
      document.removeEventListener("touchmove", blockTouch);
    };
  }, [locked]);
}

function CelebrationBody({
  resolvedTitle,
  listingTitle,
  subtitle,
  resolvedDetail,
  goToOrdersLabel,
  closeLabel,
  onGoToOrders,
  onDismiss,
}: {
  resolvedTitle: string;
  listingTitle?: string;
  subtitle?: string;
  resolvedDetail: string;
  goToOrdersLabel: string;
  closeLabel: string;
  onGoToOrders: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      data-celebration-panel
      className="flex flex-col items-center text-center gap-1"
    >
      <p className="text-4xl" aria-hidden>
        🎉
      </p>
      <h2 className="mt-2 text-xl font-semibold tracking-tight text-foreground">
        {resolvedTitle}
      </h2>
      {listingTitle ? (
        <p className="mt-1.5 text-[15px] font-medium text-foreground/90">
          {listingTitle}
        </p>
      ) : null}
      {subtitle ? (
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      ) : null}
      <p className="mt-3 w-full rounded-xl border border-emerald-500/20 bg-emerald-500/[0.08] px-3 py-2.5 text-[13px] leading-relaxed text-foreground/90">
        {resolvedDetail}
      </p>
      <div className="mt-5 flex w-full flex-col gap-2">
        <Button type="button" className="w-full" onClick={onGoToOrders}>
          {goToOrdersLabel}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={onDismiss}
        >
          {closeLabel}
        </Button>
      </div>
    </div>
  );
}

export function CelebrationOverlay({
  open,
  kind,
  title,
  listingTitle,
  subtitle,
  detail,
  onDismiss,
}: CelebrationOverlayProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isNative } = useNativePlatform();
  const isMobile = useIsMobile();
  const asSheet = isNative || isMobile;
  const L = (commonLabels(language) as { celebration?: Record<string, string> })
    ?.celebration;

  useBodyScrollLock(open && !asSheet);

  useEffect(() => {
    if (!open) return;
    return fireCelebrationBurst();
  }, [open]);

  const copy = useMemo(() => {
    const resolvedTitle =
      title ??
      (kind === "auction_won"
        ? (L?.titleWon ?? "Grattis — du vann!")
        : kind === "purchase"
          ? (L?.titlePurchase ?? "Grattis till köpet!")
          : (L?.titleGeneric ?? "Grattis!"));

    const resolvedDetail =
      detail ??
      (kind === "auction_won"
        ? (L?.detailWon ??
          "Auktionen är avslutad och du är vinnare. Fordonet registreras som reserverat hos oss — vi hör av oss med avtal och nästa steg.")
        : kind === "purchase"
          ? (L?.detailPurchase ??
            "Fordonet är nu registrerat som reserverat hos oss. Vi kontaktar dig snart med avtal och nästa steg.")
          : (L?.detailGeneric ??
            "Vi hör av oss snart med avtal och nästa steg."));

    return {
      resolvedTitle,
      resolvedDetail,
      goToOrders: L?.goToOrders ?? "Gå till ordrar",
      close: L?.close ?? "Stäng",
      dismissAria: L?.dismissAria ?? "Stäng",
    };
  }, [L, detail, kind, title]);

  const goToOrders = () => {
    onDismiss();
    navigate("/orders");
  };

  const body = (
    <CelebrationBody
      resolvedTitle={copy.resolvedTitle}
      listingTitle={listingTitle}
      subtitle={subtitle}
      resolvedDetail={copy.resolvedDetail}
      goToOrdersLabel={copy.goToOrders}
      closeLabel={copy.close}
      onGoToOrders={goToOrders}
      onDismiss={onDismiss}
    />
  );

  if (asSheet) {
    return (
      <Sheet open={open} onOpenChange={(next) => !next && onDismiss()}>
        <SheetContent
          side="bottom"
          expandable={false}
          nestedSheet={isNative}
          // Stack: dim 330 → confetti 331 → Grattis sheet 332
          overlayClassName="!z-[330]"
          className={cn(
            isNative
              ? "native-sheet-compact w-full max-w-none !z-[332]"
              : "!z-[332]",
            "gap-0",
          )}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{copy.resolvedTitle}</SheetTitle>
          </SheetHeader>
          <div className="px-1 pb-2 pt-1">{body}</div>
        </SheetContent>
      </Sheet>
    );
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[332] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-label={copy.resolvedTitle}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label={copy.dismissAria}
        onClick={onDismiss}
      />
      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-white/15 bg-background/95 p-6 shadow-2xl">
        {body}
      </div>
    </div>
  );
}
